import { useEffect, useState } from "react";
import { BarChart3, FileText, Sheet, TrendingUp, IndianRupee, Bell, Receipt } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, LineChart, Line } from "recharts";
import api, { downloadFile } from "@/lib/api";
import { fmtDate } from "@/lib/date";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const COLORS = ["#2563EB", "#7C3AED", "#DB2777", "#F59E0B", "#10B981", "#0EA5E9", "#CA8A04", "#475569"];
const CUSTOMER_MODULES = ["booking", "trip", "order", "bill", "subscription", "document", "family", "reminder"];
const AGENT_MODULES = ["enquiry", "customer", "quotation", "booking", "trip", "supplier", "rate"];
const inRange = (ds, from, to) => { if (!ds) return !from && !to; const d = String(ds).slice(0, 10); if (from && d < from) return false; if (to && d > to) return false; return true; };

export default function ReportsPage({ agent = false }) {
  const { user } = useAuth();
  const isAgent = agent || user?.role === "travel_agent";
  const [summary, setSummary] = useState(null);
  const [sales, setSales] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [bills, setBills] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });

  const qs = () => [range.from && `from=${range.from}`, range.to && `to=${range.to}`].filter(Boolean).join("&");
  useEffect(() => {
    const q = qs();
    api.get(`/reports/summary${q ? `?${q}` : ""}`).then((r) => setSummary(r.data)).catch(() => setSummary({ counts: {} }));
    if (isAgent) {
      api.get("/reports/sales").then((r) => setSales(r.data)).catch(() => {});
      api.get("/reviews/seller/summary").then((r) => setReviewStats(r.data)).catch(() => setReviewStats(null));
    } else {
      api.get("/records?type=reminder").then((r) => setReminders(r.data.records || [])).catch(() => {});
      api.get("/records?type=bill").then((r) => setBills(r.data.records || [])).catch(() => {});
    }
    /* eslint-disable-next-line */
  }, [isAgent, range.from, range.to]);

  const counts = summary?.counts || {};
  const bars = Object.entries(counts).map(([k, v]) => ({ name: k.replace(/_/g, " "), value: v }));
  const total = bars.reduce((a, b) => a + b.value, 0);
  const modules = isAgent ? AGENT_MODULES : CUSTOMER_MODULES;

  const pendingReminders = reminders.filter((r) => r.status !== "completed" && inRange((r.data || {}).due_date, range.from, range.to));
  const dueBills = bills.filter((b) => b.status !== "paid" && inRange((b.data || {}).due_date, range.from, range.to));

  const exportScope = async (scope, fmt) => {
    const q = qs();
    try { await downloadFile(`/reports/export?scope=${scope}&fmt=${fmt}${q ? `&${q}` : ""}`, `${scope}_report.${fmt}`); toast.success(`${fmt.toUpperCase()} downloaded`); }
    catch (e) { toast.error("Could not export this report."); }
  };

  return (
    <div>
      <PageHeader title="Reports & Analytics" icon={BarChart3}
        subtitle="All reports use only your actual authorized data. Filter any section by date."
        actions={<div className="flex flex-wrap items-center gap-2" data-testid="reports-controls">
          <Input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} data-testid="reports-from" className="h-9 w-[9.5rem]" aria-label="From date" />
          <span className="text-slate-400">–</span>
          <Input type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} data-testid="reports-to" className="h-9 w-[9.5rem]" aria-label="To date" />
          {(range.from || range.to) && <button onClick={() => setRange({ from: "", to: "" })} data-testid="reports-clear-range" className="text-xs font-medium text-slate-500 hover:text-slate-700">Clear</button>}
          <button onClick={() => exportScope("dashboard", "pdf")} data-testid="reports-export-pdf" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 hover:border-rose-300 hover:text-rose-500"><FileText className="h-4 w-4" /> PDF</button>
          <button onClick={() => exportScope("dashboard", "xlsx")} data-testid="reports-export-xlsx" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 hover:border-emerald-300 hover:text-emerald-600"><Sheet className="h-4 w-4" /> Excel</button>
        </div>} />

      <div className="grid gap-5 lg:grid-cols-2">
        <SectionCard title="Overview">
          {total === 0 ? <p className="py-10 text-center text-sm text-slate-400">No data in this range.</p> : (
            <div className="h-64"><ResponsiveContainer width="100%" height="100%">
              <BarChart data={bars}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} interval={0} angle={-20} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} />
                <Tooltip /><Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#2563EB" />
              </BarChart>
            </ResponsiveContainer></div>
          )}
        </SectionCard>
        <SectionCard title="Distribution">
          {total === 0 ? <p className="py-10 text-center text-sm text-slate-400">Nothing to show yet.</p> : (
            <div className="h-64"><ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={bars.filter((b) => b.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {bars.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie><Tooltip />
              </PieChart>
            </ResponsiveContainer></div>
          )}
        </SectionCard>
      </div>

      {!isAgent && (
        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <SectionCard title="Pending reminders">
            {pendingReminders.length === 0 ? <p className="py-8 text-center text-sm text-slate-400">You're all caught up.</p> : (
              <div data-testid="report-reminders">{pendingReminders.slice(0, 12).map((r) => (
                <div key={r.record_id} className="flex items-center gap-3 border-t border-slate-100 py-2.5 first:border-t-0" data-testid="report-reminder-item">
                  <Bell className="h-4 w-4 shrink-0 text-amber-500" />
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{r.title}</span>
                  <span className="text-xs text-slate-400">{fmtDate((r.data || {}).due_date) || "—"}</span>
                </div>
              ))}</div>
            )}
          </SectionCard>
          <SectionCard title="Bills due">
            {dueBills.length === 0 ? <p className="py-8 text-center text-sm text-slate-400">No bills due in this range.</p> : (
              <div data-testid="report-bills">{dueBills.slice(0, 12).map((b) => (
                <div key={b.record_id} className="flex items-center gap-3 border-t border-slate-100 py-2.5 first:border-t-0" data-testid="report-bill-item">
                  <Receipt className="h-4 w-4 shrink-0 text-rose-500" />
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{b.title}</span>
                  <span className="text-xs text-slate-400">{fmtDate((b.data || {}).due_date) || "—"}</span>
                </div>
              ))}</div>
            )}
          </SectionCard>
        </div>
      )}

      {isAgent && (
        <div className="mt-5" data-testid="sales-analytics">
          <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="card-soft p-4"><p className="flex items-center gap-1.5 text-xs text-slate-400"><IndianRupee className="h-3.5 w-3.5" /> Accepted Revenue</p><p className="mt-1 text-2xl font-extrabold text-slate-900" data-testid="sales-total">₹{(sales?.total ?? 0).toLocaleString("en-IN")}</p></div>
            <div className="card-soft p-4"><p className="text-xs text-slate-400">Accepted Quotes</p><p className="mt-1 text-2xl font-extrabold text-slate-900">{sales?.accepted ?? 0}</p></div>
            <div className="card-soft p-4"><p className="text-xs text-slate-400">Avg. Deal Value</p><p className="mt-1 text-2xl font-extrabold text-slate-900">₹{(sales?.avg ?? 0).toLocaleString("en-IN")}</p></div>
          </div>
          <SectionCard title="Revenue — last 6 months (accepted quotations)">
            {(sales?.total ?? 0) === 0 ? <p className="py-10 text-center text-sm text-slate-400">No accepted quotations yet.</p> : (
              <div className="h-64"><ResponsiveContainer width="100%" height="100%">
                <LineChart data={sales?.series || []}>
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} allowDecimals={false} width={70} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`} />
                  <Line type="monotone" dataKey="revenue" stroke="#7C3AED" strokeWidth={2.5} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer></div>
            )}
            <p className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400"><TrendingUp className="h-3.5 w-3.5" /> Revenue reflects only accepted quotations — Suffle never fabricates sales figures.</p>
          </SectionCard>
        </div>
      )}

      {isAgent && reviewStats && (
        <div className="mt-5" data-testid="reports-reviews">
          <SectionCard title="Reviews & Ratings">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="card-soft p-4"><p className="text-xs text-slate-400">Avg rating</p><p className="mt-1 text-2xl font-extrabold text-slate-900" data-testid="reports-review-avg">{reviewStats.rating_count ? `${reviewStats.rating_avg} ★` : "—"}</p></div>
              <div className="card-soft p-4"><p className="text-xs text-slate-400">Reviews</p><p className="mt-1 text-2xl font-extrabold text-slate-900">{reviewStats.rating_count || 0}</p></div>
              <div className="card-soft p-4"><p className="text-xs text-slate-400">Awaiting response</p><p className="mt-1 text-2xl font-extrabold text-slate-900">{reviewStats.needs_response || 0}</p></div>
              <div className="card-soft p-4"><p className="text-xs text-slate-400">Reputation</p><p className="mt-1 text-2xl font-extrabold text-slate-900">{reviewStats.reputation?.score != null ? `${reviewStats.reputation.score}/100` : "—"}</p></div>
            </div>
          </SectionCard>
        </div>
      )}

      <div className="mt-5 card-soft p-5" data-testid="reports-per-module">
        <h3 className="mb-3 font-brand font-semibold text-slate-800">Download detailed reports</h3>
        <p className="mb-4 text-xs text-slate-400">Each report respects the date range above (by date added).</p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {modules.map((m) => (
            <div key={m} className="flex items-center justify-between rounded-xl border border-slate-100 bg-white px-3 py-2" data-testid={`report-module-${m}`}>
              <span className="text-sm font-medium capitalize text-slate-700">{m}s</span>
              <div className="flex items-center gap-1">
                <button onClick={() => exportScope(m, "pdf")} data-testid={`report-${m}-pdf`} className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:border-rose-300 hover:text-rose-500" title="Export PDF"><FileText className="h-3.5 w-3.5" /></button>
                <button onClick={() => exportScope(m, "xlsx")} data-testid={`report-${m}-xlsx`} className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:border-emerald-300 hover:text-emerald-600" title="Export Excel"><Sheet className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
